import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { segmentQueryKeys } from "@/company/data/segmentQueryKeys";
import { ErrorResponse } from "@reflag/shared/api";
import {
  SegmentCountsDTO,
  SegmentDTO,
  SegmentMetadataDTO,
} from "@reflag/shared/segmentAPI";

const cacheTime = 5 * 60 * 1000; // 5 minutes

type QueryOptions<TQueryFnData, TData> = Omit<
  UseQueryOptions<TQueryFnData, AxiosError<ErrorResponse>, TData>,
  "queryKey" | "queryFn"
>;

type UseSegmentsOptionsType<TData> = QueryOptions<SegmentDTO[], TData>;

export function useSegments<TData = SegmentDTO[]>(
  query?: { includeDeleted?: boolean },
  options?: UseSegmentsOptionsType<TData>,
) {
  const { appId } = useEnvironment();

  const { enabled, select, ...otherOptions } = options ?? {};

  const combinedSelect = (segments: SegmentDTO[]) => {
    const prefiltered = segments.filter(
      (segment) => !segment.deletedAt || query?.includeDeleted,
    );

    return select ? select(prefiltered) : (prefiltered as unknown as TData);
  };

  return useQuery<SegmentDTO[], AxiosError<ErrorResponse>, TData>({
    queryKey: segmentQueryKeys.list(appId),
    queryFn: () =>
      api
        .get<"/apps/:appId/segments">(`/apps/${appId}/segments`)
        .then((res) => res.data),
    staleTime: cacheTime,
    placeholderData: keepPreviousData,
    select: combinedSelect,
    ...otherOptions,
    enabled: !!appId && Boolean(enabled ?? true),
  });
}

export function useSegment(segmentId: string) {
  return useSegments(
    {
      includeDeleted: true,
    },
    {
      select(segments) {
        return segments.find((segment) => segment.id === segmentId) ?? null;
      },
    },
  );
}

type UseSegmentMetadataOptionsType<TData> = QueryOptions<
  SegmentMetadataDTO[],
  TData
>;

export function useSegmentMetadata<TData = SegmentMetadataDTO[]>(
  options: UseSegmentMetadataOptionsType<TData> = {},
) {
  const { appId } = useEnvironment();
  const { enabled, ...otherOptions } = options;

  return useQuery<SegmentMetadataDTO[], AxiosError<ErrorResponse>, TData>({
    queryKey: segmentQueryKeys.metadata(appId),
    queryFn: () =>
      api
        .get<"/apps/:appId/segments/metadata">(
          `/apps/${appId}/segments/metadata`,
        )
        .then((res) => res.data),
    enabled: !!appId && Boolean(enabled ?? true),
    staleTime: cacheTime,
    placeholderData: keepPreviousData,
    ...otherOptions,
  });
}

type UseSegmentCountsOptionsType<TData> = QueryOptions<
  SegmentCountsDTO[],
  TData
>;

export function useSegmentCounts<TData = SegmentCountsDTO[]>(
  options: UseSegmentCountsOptionsType<TData> = {},
) {
  const { appId, envId } = useEnvironment();
  const { enabled, ...otherOptions } = options;

  return useQuery<SegmentCountsDTO[], AxiosError<ErrorResponse>, TData>({
    queryKey: segmentQueryKeys.counts(appId, envId),
    queryFn: () =>
      api
        .get<"/apps/:appId/segments/counts">(`/apps/${appId}/segments/counts`, {
          params: { envId: envId! },
        })
        .then((res) => res.data),
    enabled: !!appId && !!envId && Boolean(enabled ?? true),
    staleTime: cacheTime,
    placeholderData: keepPreviousData,
    ...otherOptions,
  });
}

export function useSegmentCount(
  segmentId?: string | null,
  options: Omit<
    UseSegmentCountsOptionsType<SegmentCountsDTO | null>,
    "select"
  > = {},
) {
  const { enabled, ...otherOptions } = options;

  return useSegmentCounts<SegmentCountsDTO | null>({
    ...otherOptions,
    select(counts) {
      return counts.find((count) => count.segmentId === segmentId) ?? null;
    },
    enabled: !!segmentId && Boolean(enabled ?? true),
  });
}
